/**
 * Global axios configuration.
 *
 * Defines sensible defaults for ALL axios calls in the app so that no
 * request can hang forever on slow / intermittent connections (very common
 * on Dominican 4G / shared WiFi).
 *
 * Applied once at module load (imported from src/index.js before App mounts).
 */
import axios from 'axios';

// Per-request default timeout (ms). 25s is generous enough for slow 3G/4G
// but short enough that a stalled TCP connection is detected before the
// user gives up. Auth flows can override with a longer timeout if needed.
axios.defaults.timeout = 25000;

// ============================================================================
// 429 backoff interceptor — transparent retry with exponential delay.
// The admin panel polls many endpoints every 10s; the Kubernetes ingress in
// preview rate-limits bursts. Instead of dropping requests (which shows up as
// "Failed to load" toasts) we wait and retry up to 2 times. Components stay
// blissfully unaware.
// ============================================================================
const _MAX_429_RETRIES = 2;
const _BASE_BACKOFF_MS = 1500;  // 1.5s → 3s → 4.5s

const _sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error?.response?.status;
    const config = error?.config;
    if (status !== 429 || !config) {
      return Promise.reject(error);
    }
    config.__retryCount = (config.__retryCount || 0) + 1;
    if (config.__retryCount > _MAX_429_RETRIES) {
      return Promise.reject(error);
    }
    // Honor Retry-After if the server sent it, otherwise exponential backoff.
    let delayMs = _BASE_BACKOFF_MS * config.__retryCount;
    const retryAfter = error?.response?.headers?.['retry-after'];
    if (retryAfter) {
      const seconds = parseInt(retryAfter, 10);
      if (!Number.isNaN(seconds)) {
        delayMs = seconds * 1000;
      }
    }
    // Add small jitter so concurrent retries don't sync up again.
    delayMs += Math.floor(Math.random() * 400);
    await _sleep(delayMs);
    return axios(config);
  },
);

// Helper: classify axios errors for friendlier UI messages.
// Components / hooks can import this and decide how to react.
export const classifyAxiosError = (error) => {
  // Cancelled (e.g. component unmounted)
  if (error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError') {
    return { kind: 'canceled', message: 'Solicitud cancelada' };
  }
  // Timeout
  if (error?.code === 'ECONNABORTED' || /timeout/i.test(error?.message || '')) {
    return {
      kind: 'timeout',
      message:
        'Tu conexión es muy lenta. Verifica tu internet y vuelve a intentar.',
    };
  }
  // Browser is offline / DNS / TCP refused — no response from server at all
  if (!error?.response) {
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      return {
        kind: 'offline',
        message: 'Sin conexión a internet. Te avisaremos cuando vuelva.',
      };
    }
    return {
      kind: 'network',
      message: 'No pudimos contactar al servidor. Intenta de nuevo.',
    };
  }
  // Got an HTTP response — caller should handle status code
  return {
    kind: 'http',
    status: error.response.status,
    detail: error.response.data?.detail,
    message:
      typeof error.response.data?.detail === 'string'
        ? error.response.data.detail
        : 'Error en la solicitud',
  };
};

// True only when we are sure we have NO connectivity (browser told us so).
export const isOfflineError = (error) => {
  const c = classifyAxiosError(error);
  return c.kind === 'offline' || c.kind === 'network' || c.kind === 'timeout';
};

export default axios;
